//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import axios from 'axios'
import { ethers } from 'ethers'
import { Erc20Abi } from '@shared/Abis'
import { LytConstants } from './swap/SwapNetworkProvider'
import { WNATIVE, Token, TokenAmount } from '@traderjoe-xyz/sdk-core'
import { PairV2, RouteV2, TradeV2 } from '@traderjoe-xyz/sdk-v2'
import { createPublicClient, http } from 'viem'
import { avalancheFuji } from 'viem/chains'
import { getSwapTokenConfig, getTokenMetadata, TokenMetadata } from '@shared/SwapTokenConfig'

export interface SwapQuoteRequest {
  fromTokenAddress: string
  toTokenAddress: string
  amount: string
  slippage?: string
  userWalletAddress?: string
  autoSlippage?: boolean
  maxAutoSlippagePercent?: string
}

export interface SwapQuoteResponse {
  fromTokenAddress: string
  toTokenAddress: string
  fromAmount: string
  toAmount: string
  estimatedGas: string
  gasPrice: string
  protocols: string[]
  priceImpact: string
  tradeFee?: string
  route: {
    path: string[]
    trade?: TradeV2
    mappedTokens?: {
      fromTokenAddress: string
      toTokenAddress: string
      fromTokenDecimals: number
      toTokenDecimals: number
      fromTokenSymbol: string
      toTokenSymbol: string
    }
  }
}

export interface DexService {
  getQuote(request: SwapQuoteRequest): Promise<SwapQuoteResponse>
}

export class LfjService implements DexService {
  private chainId: number
  private stageName: string
  private provider: ethers.JsonRpcProvider
  private publicClient: any

  constructor(stageName: string, chainId: number, provider: ethers.JsonRpcProvider, rpcUrl: string) {
    this.stageName = stageName
    this.chainId = chainId
    this.provider = provider
    this.publicClient = createPublicClient({
      chain: avalancheFuji,
      transport: http(rpcUrl),
    })
  }

  async getQuote(request: SwapQuoteRequest): Promise<SwapQuoteResponse> {
    try {
      const response = await this.getTraderJoeQuote(request)
      return response
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }

      throw new Error(`Failed to get LFJ quote: ${JSON.stringify(error)}`)
    }
  }

  private async getTraderJoeQuote(request: SwapQuoteRequest): Promise<SwapQuoteResponse> {
    const fromAmount = parseFloat(request.amount)
    if (fromAmount <= 0) {
      throw new Error('Invalid amount')
    }

    const tokenConfig = getSwapTokenConfig(this.stageName, this.chainId)
    const WAVAX = WNATIVE[this.chainId as keyof typeof WNATIVE]

    const createTokenInstance = (tokenMetadata: TokenMetadata): Token => {
      return new Token(
        this.chainId,
        tokenMetadata.address,
        tokenMetadata.decimals,
        tokenMetadata.symbol,
        tokenMetadata.name
      )
    }

    const fromTokenMeta = getTokenMetadata(this.stageName, this.chainId, request.fromTokenAddress)
    const toTokenMeta = getTokenMetadata(this.stageName, this.chainId, request.toTokenAddress)

    if (!fromTokenMeta) {
      throw new Error(`Unsupported from token: ${request.fromTokenAddress}`)
    }
    if (!toTokenMeta) {
      throw new Error(`Unsupported to token: ${request.toTokenAddress}`)
    }

    const TOKEN_A_INSTANCE = createTokenInstance(tokenConfig.TOKEN_A)
    const TOKEN_B_INSTANCE = createTokenInstance(tokenConfig.TOKEN_B)
    const inputToken = createTokenInstance(fromTokenMeta)
    const outputToken = createTokenInstance(toTokenMeta)

    const BASES = [WAVAX, TOKEN_B_INSTANCE, TOKEN_A_INSTANCE]

    const typedValueInParsed = ethers.parseUnits(request.amount, inputToken.decimals)
    const amountIn = new TokenAmount(inputToken, typedValueInParsed)

    const allTokenPairs = PairV2.createAllTokenPairs(inputToken, outputToken, BASES)
    const allPairs = PairV2.initPairs(allTokenPairs)
    const allRoutes = RouteV2.createAllRoutes(allPairs, inputToken, outputToken)

    const isNativeIn = false
    const isNativeOut = false

    try {
      const tradesExactIn = await TradeV2.getTradesExactIn(
        allRoutes,
        amountIn,
        outputToken,
        isNativeIn,
        isNativeOut,
        this.publicClient,
        this.chainId
      )

      if (!tradesExactIn || tradesExactIn.length === 0) {
        throw new Error('No valid trades found')
      }

      const bestTrade = tradesExactIn[0]

      if (!bestTrade || !bestTrade.route || !bestTrade.inputAmount || !bestTrade.outputAmount) {
        throw new Error('Invalid TradeV2 instance - this appears to be the fallback implementation.')
      }

      const { totalFeePct } = await bestTrade.getTradeFee()

      // Calculate gas cost: gasUnits * gasPriceGwei * 1e9 (convert gwei to wei) then to ether
      const gasUnits = LytConstants.POOL_CONFIG.GAS_ESTIMATE.SWAP
      const gasPriceGwei = 25n
      const gasCostWei = gasUnits * gasPriceGwei * 1000000000n // 1e9 to convert gwei to wei
      const gasEstimate = ethers.formatEther(gasCostWei)
      const gasPriceGweiStr = '25'

      const outputAmount = bestTrade.outputAmount.toSignificant(6)
      const validatedOutputAmount = parseFloat(outputAmount) > 0 ? outputAmount : '0.01'

      return {
        fromTokenAddress: request.fromTokenAddress,
        toTokenAddress: request.toTokenAddress,
        fromAmount: request.amount,
        toAmount: validatedOutputAmount,
        estimatedGas: gasEstimate,
        gasPrice: gasPriceGweiStr,
        protocols: ['TraderJoe V2.1'],
        priceImpact: totalFeePct.toSignificant(4),
        route: {
          path: bestTrade.route.path.map((token) => token.address),
          trade: bestTrade,
          mappedTokens: {
            fromTokenAddress: inputToken.address,
            toTokenAddress: outputToken.address,
            fromTokenDecimals: inputToken.decimals,
            toTokenDecimals: outputToken.decimals,
            fromTokenSymbol: inputToken.symbol || 'UNKNOWN',
            toTokenSymbol: outputToken.symbol || 'UNKNOWN',
          },
        },
      }
    } catch (_error) {
      const fallbackOutputAmount = (fromAmount * 0.999).toFixed(6)

      // Calculate fallback gas cost properly
      const gasUnits = LytConstants.POOL_CONFIG.GAS_ESTIMATE.SWAP
      const gasPriceGwei = 25n
      const gasCostWei = gasUnits * gasPriceGwei * 1000000000n
      const fallbackGasEstimate = ethers.formatEther(gasCostWei)

      return {
        fromTokenAddress: request.fromTokenAddress,
        toTokenAddress: request.toTokenAddress,
        fromAmount: request.amount,
        toAmount: fallbackOutputAmount,
        estimatedGas: fallbackGasEstimate,
        gasPrice: '25',
        protocols: ['Fallback'],
        priceImpact: '0.1',
        route: {
          path: [request.fromTokenAddress, request.toTokenAddress],
          mappedTokens: {
            fromTokenAddress: inputToken.address,
            toTokenAddress: outputToken.address,
            fromTokenDecimals: inputToken.decimals,
            toTokenDecimals: outputToken.decimals,
            fromTokenSymbol: fromTokenMeta.symbol,
            toTokenSymbol: toTokenMeta.symbol,
          },
        },
      }
    }
  }
}

export class OkxApiService implements DexService {
  private apiBaseUrl: string
  private apiKey: string
  private decimalsCache: Map<string, number> = new Map()
  private symbolsCache: Map<string, string> = new Map()

  constructor(apiBaseUrl: string, apiKey: string) {
    this.apiBaseUrl = apiBaseUrl
    this.apiKey = apiKey
  }

  async getQuote(request: SwapQuoteRequest): Promise<SwapQuoteResponse> {
    try {
      // Get token decimals and symbols dynamically
      const [fromTokenDecimals, toTokenDecimals, fromTokenSymbol, toTokenSymbol] = await Promise.all([
        this.getTokenDecimals(request.fromTokenAddress),
        this.getTokenDecimals(request.toTokenAddress),
        this.getTokenSymbol(request.fromTokenAddress),
        this.getTokenSymbol(request.toTokenAddress),
      ])

      const amountInWei = ethers.parseUnits(request.amount, fromTokenDecimals).toString()

      const requestWithWeiAmount = {
        ...request,
        amount: amountInWei,
      }

      const response = await axios.post(`${this.apiBaseUrl}/dex/quote`, requestWithWeiAmount, {
        headers: {
          'x-api-key': this.apiKey,
          'Content-Type': 'application/json',
        },
      })

      const quoteData = response.data

      // Enhance the response with symbol and decimal information if mappedTokens exists
      if (quoteData.route?.mappedTokens) {
        quoteData.route.mappedTokens.fromTokenSymbol = fromTokenSymbol
        quoteData.route.mappedTokens.toTokenSymbol = toTokenSymbol
        quoteData.route.mappedTokens.fromTokenDecimals = fromTokenDecimals
        quoteData.route.mappedTokens.toTokenDecimals = toTokenDecimals
      }

      return quoteData
    } catch (error) {
      throw new Error(`Failed to get OKX quote: ${error}`)
    }
  }

  private async getTokenDecimals(tokenAddress: string): Promise<number> {
    // Check cache first
    const cached = this.decimalsCache.get(tokenAddress.toLowerCase())
    if (cached !== undefined) {
      return cached
    }

    try {
      // Use ethers to query the token contract
      const provider = new ethers.JsonRpcProvider('https://api.avax.network/ext/bc/C/rpc')
      const tokenContract = new ethers.Contract(tokenAddress, ['function decimals() view returns (uint8)'], provider)
      const decimals = await tokenContract.decimals()

      // Cache the result
      this.decimalsCache.set(tokenAddress.toLowerCase(), decimals)
      return decimals
    } catch (_error) {
      // Fallback to 18 decimals if query fails
      console.warn(`Failed to get decimals for ${tokenAddress}, using 18 as fallback`)
      const fallbackDecimals = 18
      this.decimalsCache.set(tokenAddress.toLowerCase(), fallbackDecimals)
      return fallbackDecimals
    }
  }

  private async getTokenSymbol(tokenAddress: string): Promise<string> {
    // Check cache first
    const cached = this.symbolsCache.get(tokenAddress.toLowerCase())
    if (cached !== undefined) {
      return cached
    }

    try {
      // Use ethers to query the token contract
      const provider = new ethers.JsonRpcProvider('https://api.avax.network/ext/bc/C/rpc')
      const tokenContract = new ethers.Contract(tokenAddress, Erc20Abi, provider)
      const symbol = await tokenContract.symbol()

      // Cache the result
      this.symbolsCache.set(tokenAddress.toLowerCase(), symbol)
      return symbol
    } catch (_error) {
      // Fallback to address suffix if symbol query fails
      console.warn(`Failed to get symbol for ${tokenAddress}, using address as fallback`)
      const fallbackSymbol = `TOKEN_${tokenAddress.slice(-4).toUpperCase()}`
      this.symbolsCache.set(tokenAddress.toLowerCase(), fallbackSymbol)
      return fallbackSymbol
    }
  }
}

let currentDexService: LfjService | OkxApiService | null = null

export function getDexService(
  useOkx: boolean = false,
  stageName: string = 'qasierra',
  chainId: number = 43113,
  provider?: ethers.JsonRpcProvider,
  apiBaseUrl?: string,
  apiKey?: string,
  rpcUrl?: string
): LfjService | OkxApiService {
  if (useOkx && apiBaseUrl && apiKey) {
    if (!currentDexService || !(currentDexService instanceof OkxApiService)) {
      currentDexService = new OkxApiService(apiBaseUrl, apiKey)
    }
    return currentDexService
  } else {
    if (!currentDexService || !(currentDexService instanceof LfjService)) {
      const defaultProvider = provider || new ethers.JsonRpcProvider('https://api.avax-test.network/ext/bc/C/rpc')
      const defaultRpcUrl = rpcUrl || 'https://api.avax-test.network/ext/bc/C/rpc'
      currentDexService = new LfjService(stageName, chainId, defaultProvider, defaultRpcUrl)
    }
    return currentDexService
  }
}
